import { useMemo } from 'react';
import { useInvoicesPagination } from '../utils/hooks/invoiceHooks/useInvoicePagination';
import { useInvoicesSorting } from '../utils/hooks/invoiceHooks/useInvoiceSorting';
import { useInvoicesFilters } from '../utils/hooks/invoiceHooks/useInvoiceFilters';
import { useSearchBillingRemindersV2, useGetBillingReminderFilters, AGE_BUCKET_LABELS, BillingReminderSearchRequest } from '../api/endpoints/invoicesAll';
import { formatDate, formatMoney } from '../utils/tableUtils';
import { BillingReminder } from '@/app/features/InvoiceBillingReminder/billingReminder.types';

const formatDisplayDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });

export interface BillingReminderFilterOptions {
  carrierOptions: string[];
  statusOptions: string[];
  ageOptions: string[];
}

export const useBillingRemindersTable = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const pagination = useInvoicesPagination(1, 25);
  const sorting = useInvoicesSorting(pagination.resetToFirstPage);
  const { buttonFilters, handleButtonFilterChange } = useInvoicesFilters(pagination.resetToFirstPage);

  const searchRequest = useMemo((): BillingReminderSearchRequest => ({
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    filters: buttonFilters.length > 0
      ? buttonFilters.map((f) => ({ column: f.column, value: f.value }))
      : undefined,
    sort: sorting.sortConfig[0]
      ? { field: sorting.sortConfig[0].field, order: sorting.sortConfig[0].order }
      : { field: 'age', order: 'desc' },
  }), [pagination.currentPage, pagination.pageSize, buttonFilters, sorting.sortConfig]);

  const { data, isFetching, refetch } = useSearchBillingRemindersV2(searchRequest, enabled);
  const { data: filtersData } = useGetBillingReminderFilters(enabled);

  const rows: BillingReminder[] = useMemo(
    () =>
      (data?.items ?? []).map((invoice) => ({
        ...invoice,
        willPay: invoice.willPay ?? true,
        invoiceDate: formatDate(invoice.invoiceDate),
        invoiceDueDate: formatDate(invoice.invoiceDueDate),
        amount: formatMoney(invoice.amount),
        amountDue: formatMoney(invoice.amountDue),
        showWarning: invoice.urgent ?? false,
      })),
    [data?.items],
  );

  // Derive due-date range from the current response for display purposes
  const dateRangeDisplay = useMemo(() => {
    const dueDates = (data?.items ?? [])
      .map((i) => i.invoiceDueDate)
      .filter(Boolean)
      .map((d) => new Date(d!));
    if (dueDates.length === 0) return null;
    const min = new Date(Math.min(...dueDates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dueDates.map((d) => d.getTime())));
    return `${formatDisplayDate(min)} - ${formatDisplayDate(max)}`;
  }, [data?.items]);

  return {
    rows,
    totalCount: data?.totalCount ?? 0,
    paymentLinkUrl: data?.paymentLinkUrl,
    isLoading: isFetching,
    refetch,
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    handlePageChange: pagination.handlePageChange,
    handlePageSizeChange: pagination.handlePageSizeChange,
    handleOnSort: sorting.handleOnSort,
    buttonFilters,
    handleButtonFilterChange,
    availableFilterOptions: {
      carrierOptions: filtersData?.carrierNames ?? [],
      statusOptions: filtersData?.collectionStatuses ?? [],
      ageOptions: AGE_BUCKET_LABELS,
    },
    dateRangeDisplay,
  };
};
